import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const API_BASE_URL = API;
export const INGEST_URL = `${API}/ingest/counts`;
export const PING_URL = `${API}/ingest/ping`;

const http = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });

// Called when any protected request answers 401 (expired / cleared session) so the app can return to the login page.
let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};
http.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && !url.startsWith("/auth/") && onUnauthorized) onUnauthorized();
    return Promise.reject(err);
  }
);

/** Human-readable message from an axios error. */
export const errorMessage = (err, fallback = "Something went wrong") => err?.response?.data?.detail || err?.message || fallback;

// Legacy (v1) dashboard scope - still used by the Live Count Feed page helpers.
const scope = { site: null, range: null };
export const setApiScope = ({ site, range }) => {
  if (site !== undefined) scope.site = site;
  if (range !== undefined) scope.range = range;
};
export const getApiScope = () => ({ ...scope });
const q = (withRange = true) => {
  const params = {};
  if (scope.site) params.site = scope.site;
  if (withRange && scope.range) params.range = scope.range;
  return params;
};

export const api = {
  // auth
  login: async (login, password) => (await http.post("/auth/login", { login, password })).data,
  logout: async () => (await http.post("/auth/logout")).data,
  me: async () => (await http.get("/auth/me")).data,
  changePassword: async (current_password, new_password) => (await http.put("/auth/password", { current_password, new_password })).data,
  // users (admin)
  listUsers: async () => (await http.get("/users")).data,
  createUser: async (body) => (await http.post("/users", body)).data,
  updateUser: async (id, body) => (await http.put(`/users/${id}`, body)).data,
  resetUserPassword: async (id, password) => (await http.put(`/users/${id}/password`, { password })).data,
  deleteUser: async (id) => (await http.delete(`/users/${id}`)).data,
  // v2 executive analytics
  getScopes: async () => (await http.get("/v2/scopes")).data,
  getCount: async (params) => (await http.get("/v2/count", { params })).data,
  getSalesV2: async (params) => (await http.get("/v2/sales", { params })).data,
  getSummary: async (params) => (await http.get("/v2/summary", { params: { period: params.period, start: params.start, end: params.end } })).data,
  getInsightsV2: async (kind, params) => (await http.get("/v2/insights", { params: { kind, ...params } })).data,
  generateInsightsV2: async (kind, params, force = false) => (await http.post("/v2/insights/generate", { kind, ...params, force })).data,
  runNightlyInsights: async () => (await http.post("/v2/insights/nightly")).data,
  // admin: AI key usage ledger (admin-only on the server)
  getAiUsageSummary: async (params) => (await http.get("/admin/ai-usage/summary", { params })).data,
  getAiUsageLog: async (params) => (await http.get("/admin/ai-usage/log", { params })).data,
  getAiUsageRun: async (runId) => (await http.get(`/admin/ai-usage/runs/${encodeURIComponent(runId)}`)).data,
  getAiUsageRates: async () => (await http.get("/admin/ai-usage/rates")).data,
  updateAiUsageRates: async (rates, repriceHistory = false) => (await http.put("/admin/ai-usage/rates", { rates, reprice_history: repriceHistory })).data,
  exportAiUsage: async (params) => {
    const res = await http.get("/admin/ai-usage/export", { params, responseType: "blob" });
    const m = /filename="?([^";]+)"?/i.exec(res.headers?.["content-disposition"] || "");
    return { blob: res.data, filename: m ? m[1] : `zapin_ai_usage_${params.what || "log"}.${params.format || "xlsx"}` };
  },
  // calendar
  getCalendar: async (params) => (await http.get("/calendar", { params })).data,
  getForecast: async ({ scope, horizon }) => (await http.get("/v2/forecast", { params: { scope, horizon } })).data,
  getForecastBrief: async ({ scope = "all", horizon = 30 } = {}) => (await http.get("/v2/forecast/brief", { params: { scope, horizon } })).data,
  /** Downloads the forecast as CSV / Excel; resolves with { blob, filename } (filename from Content-Disposition). */
  exportForecast: async ({ scope, horizon, format }) => {
    const res = await http.get("/v2/forecast/export", { params: { scope, horizon, format }, responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    return { blob: res.data, filename: m ? m[1] : `zapin_forecast_${scope}_next${horizon}d.${format}` };
  },
  createEvent: async (body) => (await http.post("/calendar/events", body)).data,
  updateEvent: async (id, body) => (await http.put(`/calendar/events/${id}`, body)).data,
  deleteEvent: async (id) => (await http.delete(`/calendar/events/${id}`)).data,
  // sites + POS (v1 endpoints, still the source of truth for uploads)
  getSites: async () => (await http.get("/sites")).data,
  uploadLedger: async (file, siteId, mode = "merge") => {
    const fd = new FormData();
    fd.append("file", file);
    return (await http.post("/sales/ledger", fd, { params: { site: siteId, mode }, headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
  },
  deleteLedger: async (siteId) => (await http.delete("/sales/ledger", { params: { site: siteId } })).data,
  // live count feed (admin)
  getFeedStatus: async () => (await http.get("/feed/status")).data,
  getFeedBrief: async () => (await http.get("/feed/brief")).data,
  getFeedKey: async () => (await http.get("/feed/key")).data,
  rotateFeedKey: async () => (await http.post("/feed/key/rotate", { confirm: true })).data,
  getFeedDeliveries: async (limit = 25) => (await http.get("/feed/deliveries", { params: { limit } })).data,
  reimportCsv: async () => (await http.post("/feed/reimport")).data,
  // nightly digest (per store)
  getDigest: async (siteId) => (await http.get("/digest", { params: { site: siteId } })).data,
  getDigestAll: async () => (await http.get("/digest/all")).data,
  getDigestRuns: async (limit = 10) => (await http.get("/digest/runs", { params: { limit } })).data,
  runDigest: async ({ site, date, force = false, refreshInsights = true } = {}) => (await http.post("/digest/run", { site: site ?? null, date: date ?? null, force, refresh_insights: refreshInsights })).data,
  // legacy v1 reads kept for compatibility
  getMetrics: async () => (await http.get("/metrics", { params: q() })).data,
  getInsights: async () => (await http.get("/insights", { params: q() })).data,
};
